import { createClient } from '@supabase/supabase-js'
import type { DatabaseWithTypedRpc } from '@shared/types/database-extensions'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

/**
 * Generate a unique storage key per portal to prevent localStorage conflicts
 * when running multiple portals locally on different ports.
 *
 * In production, each portal runs on its own domain, so this isn't needed.
 * But locally (localhost:5173, localhost:5174, localhost:5175) they share
 * the same localStorage namespace, causing session conflicts on sign out.
 */
function getStorageKey(): string | undefined {
  // In development: use port number to differentiate
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    const port = window.location.port;
    return `sb-${port}-auth-token`;
  }

  // In production: use hostname (lexlabour.nl, lexzone.nl, lexwoo.nl)
  // This is the default Supabase behavior, so we return undefined to use default
  return undefined;
}

/**
 * Supabase client with typed RPC functions.
 *
 * Uses DatabaseWithTypedRpc which extends the auto-generated Database type
 * with properly typed return values for RPC functions like zoek_jurisprudentie.
 */
export const supabase = createClient<DatabaseWithTypedRpc>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: getStorageKey(),
    },
  }
)